import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
///

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
  ICTenorValueDateModel,
  ICFixingSourceModel,
} from "../interface/controls/models/bfx-interface-controls.models";
import {
  ActiveUserProfilePromiseService,
  ActiveUser,
  ProfileSettingKeys,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  UserPromiseLookupService,
  ClientPromiseLookupService,
  SystemConfigPromiseService,
  SysFeatures,
  ClientAccountVisibilityType,
} from "shared/cubiq-services";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
import { OEInputModel } from "../data/models/oe-input.model";
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OEDataModel } from "../data/models/oe-data.model";
import { OETimeMachineService } from "./basic/oe-time-machine.service";
import { OECoordinatorService } from "./oe-coordinator.service";
import {
  OELockingProfileModel,
  OEPrivatizationLockingProfileModel,
  OEInputLockStateItemModel,
} from "../data/models/oe-locking-profile.model";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OESyncService } from "./oe-sync.service";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OELookupService } from "./basic/oe-lookup.service";
import { OEAccountMediatorService } from "./basic/oe-account-mediator.service";
import { OEModelOperationsService } from "./model/oe-model-operations.service";
import { OEUtilityService } from "./basic/oe-utility.service";
import { OEModelSyncService } from "./model/oe-model-sync.service";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { ATDLDatasetService } from "./atdl/atdl-dataset.service";
import { OEDatasetService } from "./basic/oe-dataset.service";
import { OEPermissionsService } from "./basic/oe-permissions.service";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OEModelLockingService } from "./model/oe-model-locking.service";
import { OELoggerService } from "./basic/oe-logger.service";
import { OERateSubscriptionManagerService } from "./basic/oe-rate-subscription-manager.service";
import { BenchmarkFixingTime } from "shared/cubiq-services/lookup/model/impl/BenchmarkFixingPrimaryLookupService";
import { OEValidatorService } from "./oe-validator.service";

@Injectable()
export class OELoaderService {
  private fwdEnabled: boolean = false;
  private enterMarkupBMark: boolean = false;
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private translate: TranslateService,
    protected oeLogger: OELoggerService,
    // private settings: OESettingsService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private lookup: OELookupService,
    private coordinator: OECoordinatorService,
    private timeMachine: OETimeMachineService,
    private sync: OESyncService,
    private accountMediator: OEAccountMediatorService,
    private operations: OEModelOperationsService,
    private utility: OEUtilityService,
    private modelSync: OEModelSyncService,
    private permissionService: OEPermissionsService,
    private rateSubscriptionManagerService: OERateSubscriptionManagerService,
    private validator: OEValidatorService,
    private activeUserLookup: ActiveUserProfilePromiseService,
    private ccyPairLookup: CcyPairPromiseLookupService,
    private clientLookup: ClientPromiseLookupService,
    private userLookup: UserPromiseLookupService,
    private sysConfigService: SystemConfigPromiseService,
    private atdlDataset: ATDLDatasetService,
    private dataset: OEDatasetService,
    private locker: OEModelLockingService, //	private algoLookup: AlgoStrategyPromiseLookupService
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  private initialize() {
    this.sysConfigService.getSystemConfig(SysFeatures.fwdEnabled).then((value) => {
      this.fwdEnabled = "Y" == value || "y" == value;
    });
  }

  /**********************************************************************/
  /* ACTIVATE AND LOAD Strategy Node on the interface */

  /* Use this to add a new strategy leg */
  public activateStrategyLeg(parentRefId: string, legRefId: string): void {
    let legModelLoadData = this.getter.instance(legRefId);
    if (legModelLoadData != null && legModelLoadData instanceof OEDataModel) {
      // create interface component consumable data load array
      let data: OEDLoadingDataModel =
        this.utility.createInterfaceLoadDataFromModel(legModelLoadData);
      // get strategy leg locking profile
      let legModelLockingProfile: OELockingProfileModel =
        OEPrivatizationLockingProfileModel.getLockIntersection(
          legModelLoadData.strategy.lockProfile,
          legModelLoadData.privatizationLockProfile,
        );
      // attach RefId to order entry screen so that it listens for strategy leg
      this.coordinator.cmdAddStrategyLegRefListener(parentRefId, legRefId);
      // activate/select the strategy leg
      setTimeout(() => {
        this.coordinator.cmdActivateStrategyLegRefListener(legRefId);
      }, 50);
      // load strategy leg data
      setTimeout(() => {
        this.coordinator.cmdLoadInterfaceControlData(legRefId, data);
      }, 100);
      // apply locking profile
      setTimeout(() => {
        this.coordinator.cmdLoadInterfaceControlLockingProfile(legRefId, legModelLockingProfile);
      }, 150);
      // update the render
      setTimeout(() => {
        this.coordinator.cmdDisplayStrategyLayoutChange(legRefId, OENCommand.PANEL_SHOW_STRATEGY);
      }, 150);
    } else {
      logger.out.error(
        `ERROR: _oeActions.activateStrategyLeg(): Invalid order data model for ${legRefId}`,
      );
    }
  }

  /* Use this to switch between strategies */
  public actionLoadStrategy(refId: string): void {
    if (this.getter.has(refId)) {
      try {
        let model = this.getter.get(refId);
        this.oeLogger.outputDevLogToConsole(
          "Model instance switch to '" + refId + "'  model dump:",
          false,
          model,
        );

        let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(model);
        let lockingProfile: OELockingProfileModel =
          OEPrivatizationLockingProfileModel.getLockIntersection(
            model.strategy.lockProfile,
            model.privatizationLockProfile,
          );

        // activate screen listeners
        this.coordinator.cmdActivateStrategyLegRefListener(refId);
        setTimeout(() => {
          // load data and locking profile
          this.coordinator.cmdLoadInterfaceControlData(refId, data);
          this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, lockingProfile);
        }, 80);

        if (
          (this.getter.isEntryTypeSpot(refId) || this.getter.isEntryTypeNdf(refId)) &&
          this.getter.isOrderTypeMKT(refId)
        ) {
          setTimeout(() => {
            let mktLockingProfile = this.locker.createLockProfileForMKTStrategyLeg(refId);
            this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, mktLockingProfile);
          }, 200);
        }
      } catch (ex) {
        logger.out.error("ERROR: _oeActions.actionLoadStrategy(): Exception = ", ex);
      }
    }
  }

  /**********************************************************************/
  /* INITIAL LOAD */

  public actionLoadOrderOnInit(
    refId: string,
    applyEventUpdateSync: boolean = false,
    appliedDisplayState: OENDisplayState = null,
  ): void {
    if (this.getter.has(refId)) {
      try {
        this.setter.stopInInitialization(refId);

        let eventUpdateCmdOverride = false;
        if (applyEventUpdateSync) {
          eventUpdateCmdOverride = this.sync.applyLatestUpdateEvent(refId, true);
        }

        // TODO: Check if refId is root
        let modelData: OEDataModel = this.getter.get(refId);
        let executionTypeToLoad = modelData.executionMode;
        let modelOperationMode: OENOperationMode = modelData.operationMode;
        let modelEntryType: OENEntryType = modelData.entryType;

        setTimeout(() => {
          let strategyLegs: Array<string> = this.getter.getStrategyNodesListRoot(refId);
          if (strategyLegs.length > 1) {
            strategyLegs.forEach((legRefId) => {
              this.coordinator.cmdAddStrategyLegRefListener(refId, legRefId);
              if (this.getter.get(legRefId)) {
                // CHANGE: this.updateExecutionMode(legRefId, executionTypeToLoad);
                this.setter.setExecutionMode(refId, executionTypeToLoad);
              }
            });
            this.coordinator.cmdDisplayStrategyLayoutChange(refId, OENCommand.PANEL_SHOW_STRATEGY);
          }
        }, 200);
        // the order being loaded is active atm
        this.setter.setActivelyDisplayed(refId);
        // prepare to load data by transforming the model to input model array
        let dataToLoad: OEDLoadingDataModel =
          this.utility.createInterfaceLoadDataFromModel(modelData);
        setTimeout(() => {
          this.coordinator.cmdLoadInterfaceControlData(refId, dataToLoad);
        }, 0);
        setTimeout(() => {
          let lockProfile = OEPrivatizationLockingProfileModel.getLockIntersection(
            modelData.strategy.lockProfile,
            modelData.privatizationLockProfile,
          );
          this.performAdditionalLockingBasedOnPermissions(
            modelEntryType,
            modelOperationMode,
            lockProfile,
          );
          this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, lockProfile);
        }, 100);

        // added to fix strategy button locking for MKT orders
        setTimeout(() => {
          this.coordinator.cmdUpdateStrategyButtonsLockingWithMKTCheck(refId, true);
        }, 200);

        setTimeout(() => {
          this.modelSync.removeFromBlockUpdateList(refId);
          if (eventUpdateCmdOverride) {
            this.sendEventUpdateCmdOverride(refId);
          }
          // sync between model and display state for modify
          if (
            this.getter.isOperationModeModify(refId) &&
            !eventUpdateCmdOverride &&
            this.sync.isLatestEventModifyStateEvent(refId)
          ) {
            let modelDisplayState: OENDisplayState = this.getter.getDisplayState(refId);
            if (appliedDisplayState == null || appliedDisplayState !== modelDisplayState) {
              if (this.sync.areThereUpdateEvents(refId)) {
                this.sendEventUpdateCmdOverride(refId);
              } else {
                logger.out.error(
                  `ERROR: actionLoadOrderOnInit(): Display out of sync with model, no update events to sync.`,
                );
              }
            }
          } else if (
            this.getter.isOperationModeDuplicate(refId) ||
            this.getter.isOperationModeModifyInfo(refId)
          ) {
            this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_ORDER_ENTRY);
          } else if (this.getter.isOperationModeAmendMarkup(refId)) {
            this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_ORDER_AMEND_MARKUP);
          } else if (this.getter.isOperationModeMonitorExecution(refId)) {
            this.coordinator.cmdMonitorExecution(refId);
          }
          this.coordinator.cmdLoadingComplete(refId);
        }, 300);
      } catch (ex) {
        logger.out.error("ERROR: _oeActions.actionLoadStrategy(): Exception = ", ex);
      }
    } else {
      logger.out.error(
        `ERROR: _oeActions.actionLoadStrategy(): Model with refId: ${refId} not found.`,
      );
    }
  }

  private performAdditionalLockingBasedOnPermissions(
    entryType: OENEntryType,
    operationMode: OENOperationMode,
    lockProfile: OELockingProfileModel,
  ): void {
    if (lockProfile instanceof OELockingProfileModel) {
      // lock amend markup if no permission
      if (entryType === OENEntryType.BENCHMARK && operationMode === OENOperationMode.AMEND_MARKUP) {
        let lockProfileItem = lockProfile.getLockProfileForInputElement(OENInputControl.MARKUP);
        let permissions = this.permissionService.getSysConfigPermissions();
        if (lockProfileItem instanceof OEInputLockStateItemModel && !permissions.enterMarkupBMark) {
          let lockItemUpdate = new OEInputLockStateItemModel(
            lockProfileItem.panel,
            lockProfileItem.input,
            OENLockedState.LOCKED,
          );
          lockProfile.updateInputLockState(lockItemUpdate);
        }
      }
    }
  }
  /**********************************************************************/
  /* SINGLE INPUT LOAD */

  // used for clientTrader comments
  public propagateInputValueThroughLoad(refId: string, inputData: OEInputModel): void {
    if (inputData instanceof OEInputModel) {
      let data: Array<OEInputModel> = new Array<OEInputModel>();
      data.push(inputData);
      let loadData = new OEDLoadingDataModel(
        refId,
        this.getter.getEntryType(refId),
        false,
        data,
        null,
        true,
      );
      this.coordinator.cmdLoadInterfaceControlData(refId, loadData);
    }
  }
  /**********************************************************************/
  /* USER-DEFAULTS */

  private getEntrySettingsKey(entryType: OENEntryType): string {
    switch (entryType) {
      case OENEntryType.SPOT:
        return ProfileSettingKeys.userOrderEntrySpotSettings;
      case OENEntryType.NDF:
        return ProfileSettingKeys.userOrderEntryNdfSettings;
      case OENEntryType.ALGO:
        return ProfileSettingKeys.userOrderEntryAlgoSettings;
      case OENEntryType.NDF_ALGO:
        return ProfileSettingKeys.userOrderEntryNDFAlgoSettings;
      case OENEntryType.GAMMA:
        return ProfileSettingKeys.userOrderEntryGammaSettings;
      case OENEntryType.BENCHMARK:
        return ProfileSettingKeys.userOrderEntryBmarkSettings;
      default:
        logger.out.warn(`ERROR picking settings keys`);
        return null;
    }
  }
  /* 	private getEntryCleintTabSettingsKey(entryType:OENEntryType):string{
		switch(entryType){
			case OENEntryType.SPOT:
				return ProfileSettingKeys.userOrderEntryClientPanelActiveTabs;
			case OENEntryType.NDF:
				return ProfileSettingKeys.userOrderEntryNdfClientPanelActiveTabs;
			case OENEntryType.ALGO:
				return ProfileSettingKeys.userOrderEntryAlgoClientPanelActiveTabs;
			default:
				return ProfileSettingKeys.userOrderEntryClientPanelActiveTabs;
		}
	} */

  public loadUserDefaults(refId: string): void {
    this.activeUserLookup.getActiveUserData().then((userData: ActiveUser) => {
      let entryType = this.getter.getEntryType(refId);
      let entrySettings = this.getEntrySettingsKey(entryType);
      if (userData.profileSettings != null && userData.profileSettings.has(entrySettings)) {
        try {
          let userDefaultsData = JSON.parse(userData.profileSettings.get(entrySettings));
          this.populateModelWithUserDefaults(refId, userDefaultsData).then(
            (modelBuildComplete: boolean) => {
              if (modelBuildComplete) {
                let model = this.getter.get(refId);
                let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(
                  model,
                  false,
                );
                this.coordinator.cmdLoadInterfaceControlData(refId, data);
              } else {
                logger.out.error(
                  "ERROR: _oeActions.loadUserDefaults()-order entry : Errors while populating the model instance.",
                );
              }
            },
          );
        } catch (ex) {
          logger.out.error("ERROR: Exception:  _oeActions.loadUserDefaults(): ", ex);
        }
      } else {
        // sending the load command with null data, so the entry screen can set the isLoading flag to false
        let model = this.getter.get(refId);
        let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(model, false);
        this.coordinator.cmdLoadInterfaceControlData(refId, data);
        logger.out.warn("WARNING: _oeActions.loadUserDefaults(): Order entry settings missing!");
      }
    });
  }

  private populateModelWithUserDefaults(refId: string, userDefaultsData: any): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.getter.has(refId) && userDefaultsData != null) {
        let promiseArray = [];
        // 0 client
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.client != null) {
              this.lookup
                .client(userDefaultsData.client, true, ClientAccountVisibilityType.Full)
                .then(
                  (clientModel: CubiqTS.ClientImpl) => {
                    this.operations.updateClient(refId, clientModel);
                    this.accountMediator.processClient(clientModel);
                    Promise.all([
                      this.clientLookup.getActiveClientContacts(clientModel.clientId),
                      this.userLookup.getUsersByClientCode(clientModel.clientId),
                    ]).then(
                      (values: any[]) => {
                        if (Array.isArray(values[0])) {
                          let contacts: CubiqTS.ClientContactImpl[] = values[0];

                          let contactDefaults: CubiqTS.ClientContactImpl[] = contacts.filter(
                            (contact: CubiqTS.ClientContactImpl) => contact.isDefaultFg,
                          );

                          // CHANGE: this.updateContacts(refId, contactDefaults);
                          this.setter.setContacts(refId, contactDefaults);
                        }

                        if (Array.isArray(values[1])) {
                          let users: CubiqTS.UserImpl[] = values[1];

                          let clientTraderDefaults: CubiqTS.UserImpl[] = users.filter(
                            (user: CubiqTS.UserImpl) =>
                              user.userType === CubiqTS.UserType.CLIENT_TRADER && user.isDefault,
                          );
                          if (clientTraderDefaults.length === 1) {
                            let trader = clientTraderDefaults[0] as any;
                            trader["selected"] = true;
                            this.operations.updateClientTrader(refId, trader);
                          } else {
                            this.operations.updateClientTrader(refId, null);
                          }
                        }
                        res(true);
                      },
                      () => {
                        res(false);
                      },
                    );
                  },
                  (reject) => {
                    res(false);
                  },
                );
            } else {
              res(true);
            }
          }),
        );

        // 1 account
        promiseArray.push(
          new Promise<boolean>((res, rej) => {
            if (userDefaultsData.account != null) {
              this.lookup
                .account(userDefaultsData.account, true, ClientAccountVisibilityType.Full)
                .then(
                  (accountModel: CubiqTS.AccountImpl) => {
                    this.setter.setAccount(refId, accountModel);
                    res(true);
                  },
                  (reject) => {
                    res(false);
                  },
                );
            } else {
              res(true);
            }
          }),
        );

        // 2 CcyPair
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.ccyPair != null) {
              if (
                typeof userDefaultsData.ccyPair === "string" &&
                userDefaultsData.ccyPair.length === 6
              ) {
                this.ccyPairLookup.isPrimed().then(() => {
                  let currencyPairLookup: CcyPairLookupService = this.ccyPairLookup.getBase();
                  let ccyPairModel: CubiqTS.CurrencyPairImpl = currencyPairLookup.getCcyPair(
                    userDefaultsData.ccyPair.substr(0, 3),
                    userDefaultsData.ccyPair.substr(3, 3),
                  );
                  if (ccyPairModel != null) {
                    let ccyPair = userDefaultsData.ccyPair;
                    this.setter.setCcyPair(refId, ccyPair, ccyPairModel);
                    let rateData = this.rateSubscriptionManagerService.createInitialRateDataModel(
                      ccyPair,
                      ccyPairModel,
                    );
                    rateData.invertedCcyPair === ccyPair
                      ? rateData.invertedDisplayModel
                      : rateData.displayModel;

                    let rateValue =
                      rateData.invertedCcyPair === ccyPair
                        ? rateData.invertedRateValue
                        : rateData.rateValue;
                    this.setter.setRequestPrice(refId, rateValue);
                    res(true);
                  } else {
                    let ccyPair =
                      userDefaultsData.ccyPair.substr(3, 3) + userDefaultsData.ccyPair.substr(0, 3);
                    ccyPairModel = currencyPairLookup.getCcyPair(
                      userDefaultsData.ccyPair.substr(3, 3),
                      userDefaultsData.ccyPair.substr(0, 3),
                    );
                    if (ccyPairModel != null) {
                      // set inverted
                      logger.out.debug(
                        `DEBUG: _oeActions.populateModelWithUserDefaults(): Inverted pair set! Loaded: ${userDefaultsData.ccyPair} | found: ${ccyPair}`,
                      );
                      this.setter.setCcyPair(refId, ccyPair, ccyPairModel);
                      let rateData = this.rateSubscriptionManagerService.createInitialRateDataModel(
                        ccyPair,
                        ccyPairModel,
                      );
                      rateData.invertedCcyPair === ccyPair
                        ? rateData.invertedDisplayModel
                        : rateData.displayModel;

                      let rateValue =
                        rateData.invertedCcyPair === ccyPair
                          ? rateData.invertedRateValue
                          : rateData.rateValue;
                      this.setter.setRequestPrice(refId, rateValue);
                      res(true);
                    } else {
                      res(false);
                    }
                  }
                });
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserDefaults(): Invalid ccy pair: ${userDefaultsData.ccyPair}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 3 order type
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) {
              this.setter.setOrderType(
                refId,
                new ICAutocompleteGenericModel(
                  CubiqTS.RequestOrderType.ALGO,
                  CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.ALGO],
                ),
              );
              res(true);
            } else if (
              userDefaultsData.orderType != null &&
              userDefaultsData.orderType instanceof ICAutocompleteGenericModel
            ) {
              if (
                userDefaultsData.orderType.value != null &&
                userDefaultsData.orderType.label != null
              ) {
                this.setter.setOrderType(
                  refId,
                  new ICAutocompleteGenericModel(
                    userDefaultsData.orderType.value,
                    userDefaultsData.orderType.label,
                  ),
                );
                res(true);
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserDefaults(): Invalid orderType: ${userDefaultsData.orderType}`,
                );
                res(false);
              }
            } else if (
              userDefaultsData.orderType != null &&
              typeof userDefaultsData.orderType === "number"
            ) {
              let orderType: string = this.translate.instant(
                "RequestOrderType." + CubiqTS.RequestOrderType[userDefaultsData.orderType],
              );
              this.setter.setOrderType(
                refId,
                new ICAutocompleteGenericModel(userDefaultsData.orderType, orderType),
              );
              res(true);
            } else {
              res(true);
            }
          }),
        );
        // 4 order amount
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.orderAmount != null) {
              try {
                this.setter.setOrderAmount(
                  refId,
                  new CubiqTS.BigNumber(userDefaultsData.orderAmount),
                );
                res(true);
              } catch (ex) {
                logger.out.error(
                  `ERROR: Exception: _oeActions.populateModelWithUserDefaults(): Order amt. exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 5 client intent
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.clientIntent != null) {
              if (
                userDefaultsData.clientIntent === CubiqTS.OrderIntent.BUY ||
                userDefaultsData.clientIntent === CubiqTS.OrderIntent.SELL
              ) {
                this.setter.setClientIntent(refId, userDefaultsData.clientIntent);
                res(true);
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserDefaults(): Invalid client intent: ${userDefaultsData.clientIntent}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 6 fixed ccy
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.ccy != null && userDefaultsData.ccyPair != null) {
              // userDefaultsData.ccyPair != null - added validation due to a bug in order entry settings
              if (typeof userDefaultsData.ccy === "string" && userDefaultsData.ccy.length === 3) {
                // commented out and moved above due to a bug https://www.bcdfx.com/ijira/browse/OMS-23386
                //if(userDefaultsData.ccyPair != null){
                this.setter.setFixedCcy(
                  refId,
                  new ICFixedCcyModel(userDefaultsData.ccy, userDefaultsData.ccyPair),
                );
                res(true);
                // }else{
                // 	logger.out.error("ERROR: _oeActions.populateModelWithUserDefaults(): Fixed ccy defined but ccy pair is invalid. ccy: ", userDefaultsData.ccy, " | ccyPair:", userDefaultsData.ccyPair);
                // 	res(false);
                // }
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserDefaults(): Invalid fixed ccy: ${userDefaultsData.ccy}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 7 privateOrder
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.privateOrder != null) {
              if (typeof userDefaultsData.privateOrder === "boolean") {
                this.setter.setPrivateOrder(refId, userDefaultsData.privateOrder);
                res(true);
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserDefaults(): Invalid private order: ${userDefaultsData.privateOrder}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 8 good from
        promiseArray.push(
          new Promise<boolean>((res) => {
            try {
              if (
                userDefaultsData.goodFrom != null &&
                userDefaultsData.goodFrom !== "" &&
                (this.timeMachine.isMarketTimeConstantNameValid(userDefaultsData.goodFrom) ||
                  this.timeMachine.isMarketTimeConstantIdValid(userDefaultsData.goodFrom))
              ) {
                let marketTimeConstant = this.timeMachine.getMarketTimeConstantImpl(
                  userDefaultsData.goodFrom,
                );
                if (marketTimeConstant instanceof CubiqTS.MarketTimeConstantImpl) {
                  this.setter.setGoodFrom(
                    refId,
                    this.timeMachine.createModelFromMarketTimeConstantImpl(marketTimeConstant),
                  );
                }
              } else if (userDefaultsData.goodFrom === "NOW") {
                this.setter.setGoodFrom(
                  refId,
                  this.timeMachine.createModelFromDayOrGtcOrNowStringForGoodFromUntil(
                    userDefaultsData.goodFrom,
                  ),
                );
              }
              res(true);
            } catch (ex) {
              logger.out.error(
                `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Good From exception: %O`,
                ex,
              );
              res(false);
            }
          }),
        );
        // 9 good until
        promiseArray.push(
          new Promise<boolean>((res) => {
            try {
              if (
                userDefaultsData.goodUntil != null &&
                userDefaultsData.goodUntil !== "" &&
                (this.timeMachine.isMarketTimeConstantNameValid(userDefaultsData.goodUntil) ||
                  this.timeMachine.isMarketTimeConstantIdValid(userDefaultsData.goodUntil))
              ) {
                let marketTimeConstant = this.timeMachine.getMarketTimeConstantImpl(
                  userDefaultsData.goodUntil,
                );
                if (marketTimeConstant instanceof CubiqTS.MarketTimeConstantImpl) {
                  let goodUntil =
                    this.timeMachine.createModelFromMarketTimeConstantImpl(marketTimeConstant);
                  this.setter.setGoodUntil(refId, goodUntil);
                }
              } else if (
                userDefaultsData.goodUntil === "DAY" ||
                userDefaultsData.goodUntil === "GTC"
              ) {
                // hack
                this.setter.setGoodUntil(
                  refId,
                  this.timeMachine.createModelFromDayOrGtcOrNowStringForGoodFromUntil(
                    userDefaultsData.goodUntil,
                  ),
                );
              }
              res(true);
            } catch (ex) {
              logger.out.error(
                `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Good Until exception: %O`,
                ex,
              );
              res(false);
            }
          }),
        );

        // 10 value date tenor
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData.valueDate != null && userDefaultsData.valueDate !== "") {
              try {
                if (!this.fwdEnabled) res(true);
                else {
                  if (userDefaultsData.ccyPair) {
                    this.ccyPairLookup
                      .getCcyPair(
                        userDefaultsData.ccyPair.substr(3, 3),
                        userDefaultsData.ccyPair.substr(0, 3),
                      )
                      .then((cPair) => {
                        let tn: CubiqTS.Tenor = userDefaultsData.valueDate; //BfxLabelHelper.getTenorFromLabel(userDefaultsData.valueDate);
                        this.setter.setValueDate(refId, new ICTenorValueDateModel(tn));
                        if (tn !== CubiqTS.Tenor.SPOT) {
                          if (
                            (this.getter.isEntryTypeSpot(refId) && cPair.allowFwdFwdTriggerFg) ||
                            (this.getter.isEntryTypeNdf(refId) && cPair.allowNdfFwdTriggerFg)
                          )
                            this.setter.setTrigger(
                              refId,
                              new ICAutocompleteGenericModel(
                                CubiqTS.RequestTrackingType.OUTRIGHT_RATE,
                                "Outright",
                              ),
                            );
                          else
                            this.setter.setTrigger(
                              refId,
                              new ICAutocompleteGenericModel(
                                CubiqTS.RequestTrackingType.SPOT_RATE,
                                "Spot",
                              ),
                            );
                        }
                        res(true);
                      });
                  } else {
                    res(false);
                  }
                }
              } catch (ex) {
                logger.out.error(
                  `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Value Date exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 11 algo type
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) {
              try {
                if (
                  userDefaultsData.algoType != null &&
                  typeof userDefaultsData.algoType === "string"
                ) {
                  let dataset = this.atdlDataset.getTradableAlgosForAutoComplete();
                  let algoModel = dataset.find((item) => item.value === userDefaultsData.algoType);
                  if (algoModel instanceof ICAutocompleteGenericModel) {
                    this.setter.setAlgoType(refId, algoModel);
                  }
                }
                res(true);
              } catch (ex) {
                logger.out.error(
                  `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Algo type exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );
        // 12 fixing code
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (
              userDefaultsData.fixingCode != null &&
              typeof userDefaultsData.fixingCode === "string"
            ) {
              if (this.getter.isEntryTypeBenchmark(refId)) {
                try {
                  let fixingCodeModel = this.dataset.bmFixingSources.dataset.find(
                    (item) => item.value === userDefaultsData.fixingCode,
                  );
                  if (fixingCodeModel instanceof ICFixingSourceModel) {
                    this.setter.setFixingCode(refId, fixingCodeModel);
                  }
                  res(true);
                } catch (ex) {
                  logger.out.error(
                    `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Fixing code exception: %O`,
                    ex,
                  );
                  res(false);
                }
              } else {
                try {
                  let fixingCodeModel = this.dataset.fixingSources.dataset.find(
                    (item) => item.value === userDefaultsData.fixingCode,
                  );
                  if (fixingCodeModel instanceof ICFixingSourceModel) {
                    this.setter.setFixingSource(refId, fixingCodeModel);
                  }
                  res(true);
                } catch (ex) {
                  logger.out.error(
                    `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Fixing code exception: %O`,
                    ex,
                  );
                  res(false);
                }
              }
            } else {
              res(true);
            }
          }),
        );

        // 13 Delta
        promiseArray.push(
          new Promise<boolean>((res) => {
            let defs: any = userDefaultsData;
            if (defs.delta != null && typeof defs.delta === "number") {
              try {
                this.setter.setDelta(refId, defs.delta);
                res(true);
              } catch (ex) {
                logger.out.error(
                  `EXCEPTION: _oeActions.populateModelWithUserDefaults(): ` + `Delta exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );

        // 14 Rungs above
        promiseArray.push(
          new Promise<boolean>((res) => {
            let defs: any = userDefaultsData;
            if (defs.rungsAbove != null && typeof defs.rungsAbove === "number") {
              try {
                this.setter.setRungsAbove(refId, defs.rungsAbove);
                res(true);
              } catch (ex) {
                logger.out.error(
                  `EXCEPTION: _oeActions.populateModelWithUserDefaults(): ` +
                    `Rungs above exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );

        // 15 Rungs below
        promiseArray.push(
          new Promise<boolean>((res) => {
            let defs: any = userDefaultsData;
            if (defs.rungsBelow != null && typeof defs.rungsBelow === "number") {
              try {
                this.setter.setRungsBelow(refId, defs.rungsBelow);
                res(true);
              } catch (ex) {
                logger.out.error(
                  `EXCEPTION: _oeActions.populateModelWithUserDefaults(): ` +
                    `Rungs below exception: %O`,
                  ex,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );

        // 16 Fixing time
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (
              userDefaultsData.fixingTime != null &&
              typeof userDefaultsData.fixingTime === "string"
            ) {
              if (this.getter.isEntryTypeBenchmark(refId)) {
                try {
                  let fixingCodeModel = this.dataset.bmFixingSources.allBmFixings.find(
                    (item) => item.benchmarkCd === userDefaultsData.fixingCode,
                  );

                  if (fixingCodeModel instanceof CubiqTS.BenchmarkFixingImpl) {
                    let time: string = userDefaultsData.fixingTime;
                    let timeZone: string = null;

                    if (time.includes("/")) {
                      let times: string[] = time.split("/");
                      time = times[0];
                      timeZone = times[1];
                    }

                    let fixingTime: BenchmarkFixingTime = fixingCodeModel.rules
                      .map((r) => new BenchmarkFixingTime(r.localTime, r.timezoneCd))
                      .find(
                        (t) => t.value === time && (timeZone == null || t.timezoneCd === timeZone),
                      );

                    if (fixingTime != null) {
                      this.setter.setFixing(
                        refId,
                        fixingCodeModel.benchmarkCd,
                        fixingTime.value + "/" + fixingTime.timezoneCd,
                        fixingTime.timezoneCd,
                      );
                    }
                  }
                  res(true);
                } catch (ex) {
                  logger.out.error(
                    `EXCEPTION: _oeActions.populateModelWithUserDefaults(): Fixing code exception: %O`,
                    ex,
                  );
                  res(false);
                }
              }
            } else {
              res(true);
            }
          }),
        );

        Promise.all(promiseArray)
          .then((values: Array<boolean>) => {
            let clientLoadComplete: boolean = values[0];
            let accountLoadComplete: boolean = values[1];
            let ccyPairLoadComplete: boolean = values[2];
            let orderTypeLoadComplete: boolean = values[3];
            let orderAmountLoadComplete: boolean = values[4];
            let clientIntentLoadComplete: boolean = values[5];
            let fixedCcyLoadComplete: boolean = values[6];
            let privateOrderLoadComplete: boolean = values[7];
            let goodFromLoadComplete: boolean = values[8];
            let goodUntilLoadComplete: boolean = values[9];
            let valueDateLoadComplete: boolean = values[10];
            let algoTypeLoadComplete: boolean = values[11];
            let fixingCodeLoadComplete: boolean = values[12];
            let deltaComplete: boolean = values[13];
            let rungsAboveComplete: boolean = values[14];
            let rungsBelowComplete: boolean = values[15];
            let fixingTimeComplete: boolean = values[16];

            if (
              clientLoadComplete &&
              accountLoadComplete &&
              ccyPairLoadComplete &&
              orderTypeLoadComplete &&
              orderAmountLoadComplete &&
              clientIntentLoadComplete &&
              fixedCcyLoadComplete &&
              privateOrderLoadComplete &&
              goodFromLoadComplete &&
              goodUntilLoadComplete &&
              valueDateLoadComplete &&
              algoTypeLoadComplete &&
              fixingCodeLoadComplete &&
              deltaComplete &&
              rungsAboveComplete &&
              rungsBelowComplete &&
              fixingTimeComplete
            ) {
              resolve(true);
            } else {
              /* The commented code below is a mechanism which cancels (handled in the invoking functions)
							the population of entry defaults if any of the order entry defaults parameters are not loaded
							properly in the model, thats replaced with a warning that displays the paramter that failed
							and the rest are loaded.  */

              //resolve(false);

              /* load what was set in the model. */
              logger.out.debug(
                "WARNING: _oeActions.populateModelWithUserDefaults(): " +
                  (!clientLoadComplete ? " CLIENT => FAIL!" : "") +
                  (!accountLoadComplete ? " ACCOUNT => FAIL!" : "") +
                  (!ccyPairLoadComplete ? " CCY PAIR => FAIL!" : "") +
                  (!orderTypeLoadComplete ? " ORDER TYPE => FAIL!" : "") +
                  (!orderAmountLoadComplete ? " ORDER AMOUNT => FAIL!" : "") +
                  (!clientIntentLoadComplete ? " CLIENT INTENT => FAIL!" : "") +
                  (!fixedCcyLoadComplete ? " FIXED CCY => FAIL!" : "") +
                  (!privateOrderLoadComplete ? " PRIVATE => FAIL!" : "") +
                  (!goodFromLoadComplete ? " GOOD FROM => FAIL!" : "") +
                  (!goodUntilLoadComplete ? "GOOD UNTIL => FAIL!" : "") +
                  (!valueDateLoadComplete ? "VALUE DATE => FAIL!" : "") +
                  (!algoTypeLoadComplete ? "ALGO TYPE => FAIL!" : "") +
                  (!fixingCodeLoadComplete ? "FIXING CODE => FAIL!" : ""),
                !deltaComplete ? "DELTA => FAIL!" : "",
                !rungsAboveComplete ? "RUNGS ABOVE => FAIL!" : "",
                !rungsBelowComplete ? "RUNGS BELOW => FAIL!" : "",
                !fixingTimeComplete ? "FIXING TIME => FAIL!" : "",
              );

              resolve(true);
            }
          })
          .catch((reason) => {
            logger.out.error(
              `ERROR: _oeActions.populateModelWithUserDefaults(): Internal error: exception: %O`,
              reason,
            );
            resolve(false);
          });
      } else {
        logger.out.info(
          `_oeActions.populateModelWithUserDefaults(): RefId or User Defaults missing`,
        );
        resolve(false);
      }
    });
  }

  private sendEventUpdateCmdOverride(refId: string): void {
    /* Application of eventUpdate sync on display command */
    let data = this.sync.latestEventUpdate_getData(refId);
    let cmd = this.sync.latestEventUpdate_getCmd(refId);
    let lockInterface = this.sync.latestEventUpdate_getLockInterface(refId);
    let lockActions = this.sync.latestEventUpdate_getLockActions(refId);
    this.sync.resetEventUpdateQueue(refId);

    if (cmd != null) {
      logger.out.warn(
        `DEV-TEST: Applying latestEvent: ref '${refId}'| cmd: ${
          OENCommand[cmd]
        } | data: ${data} | locked: ${lockInterface != null && lockInterface ? "YES" : "NO"}`,
      );
      this.coordinator.cmdChangeDisplayState(refId, cmd, data);
      if (lockInterface != null) {
        lockInterface
          ? this.coordinator.cmdLockInterfaceControls(refId, lockActions)
          : this.coordinator.cmdUnlockInterfaceControls(refId);
      }
    } else {
      logger.out.error(
        `ERROR: Applying latestEvent fail! CMD not defined, event:`,
        this.sync.peekLatestEventUpdate(refId),
      );
    }
  }

  /**********************************************************************/
  /* TABS */

  private populateModelWithUserActiveTabs(refId: string, userDefaultsData: any): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.getter.has(refId) && userDefaultsData != null) {
        let promiseArray = [];

        // 0 client panel active tab
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData != null && userDefaultsData.clientActiveTab != null) {
              if (
                typeof userDefaultsData.clientActiveTab === "string" &&
                userDefaultsData.clientActiveTab.length > 0 &&
                userDefaultsData.clientActiveTab.length < 5
              ) {
                this.setter.setClientActiveTab(refId, userDefaultsData.clientActiveTab);
                res(true);
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserActiveTabs(): Active client tab defined but invalid. tab name: ${userDefaultsData.clientActiveTab}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );

        // 1 account panel active tab
        promiseArray.push(
          new Promise<boolean>((res) => {
            if (userDefaultsData != null && userDefaultsData.accountActiveTab != null) {
              if (
                typeof userDefaultsData.accountActiveTab === "string" &&
                userDefaultsData.accountActiveTab.length > 0 &&
                userDefaultsData.accountActiveTab.length < 5
              ) {
                this.setter.setAccountActiveTab(refId, userDefaultsData.accountActiveTab);
                res(true);
              } else {
                logger.out.error(
                  `ERROR: _oeActions.populateModelWithUserActiveTabs(): Active client tab defined but invalid. tab name: ${userDefaultsData.accountActiveTab}`,
                );
                res(false);
              }
            } else {
              res(true);
            }
          }),
        );

        Promise.all(promiseArray)
          .then((values: Array<boolean>) => {
            let clientActiveTabLoadComplete: boolean = values[0];
            let accountActiveTabLoadComplete: boolean = values[1];
            if (clientActiveTabLoadComplete && accountActiveTabLoadComplete) {
              resolve(true);
            } else {
              resolve(false);
            }
          })
          .catch((reason) => {
            logger.out.error(
              `ERROR: _oeActions.populateModelWithUserActiveTabs(): Internal error: exception = %O`,
              reason,
            );
            resolve(false);
          });
      } else {
        logger.out.error(
          `ERROR: _oeActions.populateModelWithUserActiveTabs(): RefId or User Defaults missing`,
        );
        resolve(false);
      }
    });
  }
}
